import React, { useState, useRef, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import {
  DcFlexBox,
  DcText,
  DcTag,
  DcIcon,
  PixelSpinner,
  colors,
  DcButton,
  DcInput,
} from 'pixel-base'
import { useApi } from 'shared/hooks/useApi'
import { get, put } from 'utils/api-routes.util'
import { CheckIcon } from 'utils/icons/check-icon'
import { CloseIcon } from 'utils/icons/close-icon'

interface OrderItem {
  id: number
  sku: string
  qty: string
  name: string
  price: string
  image: string
  is_packed: boolean
}

interface Order {
  order_id: string
  status: string
  cod_amount: string
  total_pieces: string
  items: OrderItem[]
}

interface Dispatch {
  dispatchId: string
  dispatch_name: string
  dispatch_status: string
  dispatch_time: string
  warehouse_id: string
  warehouse_name: string
  total_packages_weight: string
}

interface OrderDetails {
  dispatch: Dispatch
  order: Order
}

interface DispatchItemDetailsProps {
  dispatchId: number
  item: {
    order_id: number
    tracking_id: string
    courier_company: string
    courier_id: string
    is_packed: boolean
    id: number
    eoct_id: number
    first_name: string
    no_of_pieces: number
    city: string
    cod_amount: number
    customer_notes: string
    is_scan_verified: boolean
    print_customer_note_on_invoice: boolean
    print_customer_note_on_shipping_label: boolean
    processing_time: number
    source_order_id: number
  }
  onClose: () => void
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return { bg: colors.blueDark, text: colors.white } // Darker Blue
    case 'pending':
      return { bg: colors.grayDarker, text: colors.white } // Darker Gray
    case 'processing':
      return { bg: colors.chartCyan, text: colors.white } // Darker Cyan
    case 'failed':
      return { bg: colors.danger, text: colors.white } // Darker Red
    case 'cancelled':
      return { bg: colors.purple, text: colors.white } // Darker Purple
    case 'delivered':
      return { bg: colors.success, text: colors.white } // Darker Green
    default:
      return { bg: colors.grayMedium, text: colors.white }
  }
}

const formatDateTime = (dateTimeStr: string) => {
  const date = new Date(dateTimeStr)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const DispatchItemDetails: React.FC<DispatchItemDetailsProps> = ({
  dispatchId,
  item,
  onClose,
}) => {
  const [scanValue, setScanValue] = useState('')
  const [scanning, setScanning] = useState(false)
  const [scanMessage, setScanMessage] = useState('')
  const [scanMessageType, setScanMessageType] = useState<
    'success' | 'error' | ''
  >('')
  const scanInputRef = useRef<HTMLInputElement>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {
    data: orderDetails,
    loading,
    error,
  } = useApi<OrderDetails>(
    () => get(`/dispatch/${dispatchId}/orders/${item.order_id}`),
    {
      skip: !dispatchId || !item.order_id,
      deps: [dispatchId, item.order_id],
    },
  )

  // Manual state for API calls
  const [updatingPackedStatus, setUpdatingPackedStatus] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)

  // Check if all items are packed and auto-close

  const triggerFocus = () => {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus()
        // Also select any existing text for easy replacement
        scanInputRef.current.select()
      }
    }, 50)
  }

  // Focus scan input when component mounts
  useEffect(() => {
    triggerFocus()

    // Cleanup function to clear debounce timeout
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [loading, orderDetails])

  // Additional focus effect to ensure input stays focused
  useEffect(() => {
    if (!loading && orderDetails) {
      triggerFocus()
    }
  }, [loading, orderDetails])

  // Focus effect when scanning state changes
  useEffect(() => {
    if (!scanning && !updatingPackedStatus) {
      triggerFocus()
    }
  }, [scanning, updatingPackedStatus])

  // Monitor items for auto-close when all are packed

  // Removed old useEffect hooks that were handling useApi responses

  const handleScan = async (value: string) => {
    if (!value.trim()) return

    setScanning(true)
    setScanMessage('')
    setScanMessageType('')
    setUpdateError(null)

    try {
      // Find the line item with matching SKU
      const matchingItem = orderDetails?.order.items.find(
        (orderItem) =>
          orderItem.sku.toLowerCase() === value.trim().toLowerCase(),
      )

      if (!matchingItem) {
        setScanMessage('SKU not found in this order')
        setScanMessageType('error')
        setScanValue('')
        setScanning(false)
        return
      }

      // Make the API call manually
      setUpdatingPackedStatus(true)
      const response = await put('/order/lineitem/packed-status', {
        line_item_id: matchingItem.id,
        is_packed: 1,
      }).toPromise()

      if (response.response.message) {
        setScanMessage(`Successfully marked ${matchingItem.sku} as packed!`)
        setScanMessageType('success')
        setScanValue('')

        // Delay focus to ensure state updates are processed
        // setTimeout(() => {
        triggerFocus()
        // }, 100)

        // Update the item's packed status in the local state
        if (orderDetails?.order.items) {
          const itemToUpdate = orderDetails.order.items.find(
            (orderItem) => orderItem.sku === matchingItem.sku,
          )
          if (itemToUpdate) {
            itemToUpdate.is_packed = true
          }

          // Check if all items are now packed and auto-close
          const allPacked = orderDetails.order.items.every(
            (item) => item.is_packed,
          )
          if (allPacked) {
            // Auto-close after a short delay to show success message
            setTimeout(() => {
              onClose()
            }, 1500)
          }
        }
      } else {
        setScanMessage(response.response.error)
        setScanMessageType('error')
        setUpdateError('API call failed')
      }
    } catch (error) {
      setScanMessage('Failed to update packed status. Please try again.')
      setScanMessageType('error')
      setUpdateError('API call failed')
    } finally {
      setScanning(false)
      setUpdatingPackedStatus(false)

      // Clear message after 3 seconds
      setTimeout(() => {
        setScanMessage('')
        setScanMessageType('')
        setUpdateError(null)
      }, 3000)
    }
  }

  const handleScanKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan(scanValue)
    }
  }

  const statusColor = getStatusColor(orderDetails?.order.status || 'open')

  if (loading) {
    return (
      <DcFlexBox
        style={{
          flexDirection: 'column',
          gap: '16px',
          padding: '24px',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <PixelSpinner isShow={true} />
      </DcFlexBox>
    )
  }

  if (error) {
    return (
      <DcFlexBox
        style={{
          flexDirection: 'column',
          gap: '16px',
          padding: '24px',
          width: '100%',
        }}
      >
        <DcText style={{ color: 'red' }}>{error}</DcText>
        <DcIcon icon="times" onClick={onClose} style={{ cursor: 'pointer' }} />
      </DcFlexBox>
    )
  }

  return (
    <DcFlexBox
      style={{
        flexDirection: 'column',
        gap: '16px',
        padding: '0px',
        width: '100%',
        height: '100%',
      }}
    >
      <DcFlexBox
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '4px 0',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          height: '32px',
        }}
      >
        <DcFlexBox style={{ flexDirection: 'column', gap: '4px' }}>
          <DcText style={{ fontSize: '14px', fontWeight: '600' }}>
            Order Details
          </DcText>
          <DcText style={{ fontSize: '12px', color: colors.grayTextMedium }}>
            {orderDetails?.dispatch.dispatch_time
              ? formatDateTime(orderDetails.dispatch.dispatch_time)
              : '-'}
          </DcText>
        </DcFlexBox>
        <DcIcon icon="times" onClick={onClose} style={{ cursor: 'pointer' }} />
      </DcFlexBox>
      {/* Scanning Section */}
      <DcFlexBox
        style={{
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          padding: '16px',
          backgroundColor: colors.grayLighter,
          borderRadius: '8px',
          border: `2px solid ${colors.primary}`,
        }}
        height="auto"
      >
        <DcText
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: colors.primary,
            textAlign: 'center',
          }}
        >
          📱 Scan Product SKU to Mark as Packed
        </DcText>

        <DcFlexBox
          style={{
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DcInput
            ref={scanInputRef}
            name="scanInput"
            value={scanValue}
            onChange={(e) => {
              const value = e.target.value
              setScanValue(value)

              // Clear any existing timeout
              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
              }

              // Debounce the scan to prevent multiple API calls
              debounceTimeoutRef.current = setTimeout(() => {
                if (value.trim()) {
                  handleScan(value)
                }
              }, 100) // Increased delay to ensure complete barcode capture
            }}
            onKeyPress={handleScanKeyPress}
            placeholder="Scan or type SKU..."
            style={{
              flex: 1,
              maxWidth: '300px',
            }}
            disabled={scanning || updatingPackedStatus}
            autoFocus={true}
          />
        </DcFlexBox>

        {scanMessage && (
          <DcFlexBox
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor:
                scanMessageType === 'success'
                  ? colors.successLight
                  : colors.dangerLight,
              border: `1px solid ${
                scanMessageType === 'success' ? colors.success : colors.danger
              }`,
              justifyContent: 'center',
            }}
          >
            <DcText
              style={{
                color:
                  scanMessageType === 'success'
                    ? colors.success
                    : colors.danger,
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              {scanMessage}
            </DcText>
          </DcFlexBox>
        )}
      </DcFlexBox>

      {/* Summary Section */}
      <DcFlexBox
        style={{
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          padding: '12px',
          backgroundColor: colors.grayLighter,
          borderRadius: '8px',
          height: 'auto',
        }}
      >
        <DcText
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: colors.grayTextMedium,
          }}
        >
          Summary
        </DcText>
        <DcFlexBox
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            width: '100%',
          }}
        >
          <DcFlexBox
            style={{
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <DcText style={{ fontSize: '14px', color: colors.grayTextMedium }}>
              Tracking ID
            </DcText>
            <DcText style={{ fontSize: '16px' }}>{item.tracking_id}</DcText>
          </DcFlexBox>
          <DcFlexBox
            style={{
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <DcText style={{ fontSize: '14px', color: colors.grayTextMedium }}>
              Courier
            </DcText>
            <DcText style={{ fontSize: '16px' }}>{item.courier_company}</DcText>
          </DcFlexBox>
          <DcFlexBox
            style={{
              flexDirection: 'column',
              gap: '4px',
              width: 'fit-content',
            }}
          >
            <DcText style={{ fontSize: '14px', color: colors.grayTextMedium }}>
              Status
            </DcText>
            <DcTag color={statusColor.bg} textColor={statusColor.text}>
              {orderDetails?.order.status || 'open'}
            </DcTag>
          </DcFlexBox>
          <DcFlexBox
            style={{
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <DcText style={{ fontSize: '14px', color: colors.grayTextMedium }}>
              Warehouse
            </DcText>
            <DcText style={{ fontSize: '16px' }}>
              {orderDetails?.dispatch.warehouse_name}
            </DcText>
          </DcFlexBox>
          <DcFlexBox
            style={{
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <DcText style={{ fontSize: '14px', color: colors.grayTextMedium }}>
              Dispatch Name
            </DcText>
            <DcText style={{ fontSize: '16px' }}>
              {orderDetails?.dispatch.dispatch_name}
            </DcText>
          </DcFlexBox>
        </DcFlexBox>
      </DcFlexBox>

      {/* Order Items Section */}
      <DcFlexBox
        style={{
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          height: 'auto',
        }}
      >
        <DcFlexBox
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 'auto',
          }}
        >
          <DcText
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: colors.grayTextMedium,
            }}
          >
            Order Items
          </DcText>
          <DcText
            style={{
              fontSize: '12px',
              color: colors.grayTextMedium,
            }}
          >
            {orderDetails?.order.items?.filter((item) => item.is_packed)
              .length || 0}{' '}
            of {orderDetails?.order.items?.length || 0} packed
          </DcText>
        </DcFlexBox>
        {orderDetails?.order.items && orderDetails.order.items.length > 0 ? (
          <DcFlexBox
            height={isMobile ? 'calc(100vh - 700px)' : 'auto'}
            style={{
              flexDirection: 'column',
              width: '100%',
              gap: '8px',
              overflow: 'auto',
            }}
          >
            {orderDetails.order.items.map((item) => (
              <DcFlexBox
                key={item.sku}
                flexDirection={isMobile ? 'column' : 'row'}
                style={{
                  padding: '8px',
                  borderBottom: `1px solid ${colors.grayBorderLight}`,
                  width: '100%',
                  gap: '12px',
                  alignItems: 'center',
                  backgroundColor: item.is_packed
                    ? colors.successLight
                    : 'transparent',
                  borderRadius: '6px',
                }}
              >
                <DcFlexBox style={{ width: '100%', height: 'auto' }}>
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                      width="40px"
                    />
                  ) : (
                    <DcIcon
                      icon="image"
                      style={{ color: 'gray', fontSize: '20px' }}
                    />
                  )}
                  &nbsp;
                  <DcText style={{ flex: 1 }}>{item.name}</DcText>
                </DcFlexBox>
                <DcFlexBox
                  style={{
                    flex: 1,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <DcText style={{ width: '120px' }}>{item.sku}</DcText>
                  <DcText style={{ width: '80px', textAlign: 'center' }}>
                    {item.qty}
                  </DcText>
                  <DcText style={{ width: '100px' }}>
                    PKR {Number(item.price).toLocaleString()}
                  </DcText>
                  <DcText style={{ width: 'fit-content' }}>
                    {item.is_packed ? (
                      <DcButton variant="link" size="sm" tooltip="Packed">
                        <CheckIcon size={16} color={colors.success} />
                      </DcButton>
                    ) : (
                      <DcButton variant="link" size="sm" tooltip="Not Packed">
                        <CloseIcon size={16} color={colors.danger} />
                      </DcButton>
                    )}
                  </DcText>
                </DcFlexBox>
              </DcFlexBox>
            ))}
          </DcFlexBox>
        ) : (
          <DcText style={{ color: 'gray' }}>No items found</DcText>
        )}
      </DcFlexBox>
    </DcFlexBox>
  )
}

export default DispatchItemDetails
